import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Snackbar } from '../../../state/Snackbar/SnackbarProvider';
import { IconCancel } from '../Icon';
import { Action, Close, Content, Item } from './wrapper';

const TIMEOUT = 5000;

interface SnackbarItemProps {
  snackbar: Snackbar;
  removeSnackbar: (id: number) => void;
  transform: (height: number) => void;
  disableRemove?: boolean;
}

const SnackbarItem = ({
  snackbar,
  removeSnackbar,
  transform,
  disableRemove,
}: SnackbarItemProps) => {
  const { t } = useTranslation(['common']);
  const onClick = useCallback(() => removeSnackbar(snackbar.id), []);
  const ref = useRef<HTMLDivElement | null>(null);

  const hasTimeout = !disableRemove && !snackbar.infinite; // Default snackbar, will be removed after timeout
  const [enableTransition] = useState(!hasTimeout); // Enable transition for default snackbar, when snackbar is infinite and is updated (pending => success/error)

  useEffect(() => {
    if (hasTimeout) {
      setTimeout(() => {
        removeSnackbar(snackbar.id);
      }, TIMEOUT);

      setTimeout(() => {
        if (ref.current?.clientHeight) {
          transform(ref.current.clientHeight);
        }
      }, TIMEOUT - 300);

      setTimeout(() => {
        if (ref.current) {
          ref.current.style.opacity = '0';
          ref.current.style.transition = 'opacity 300ms ease';
        }
      }, TIMEOUT - 600);
    }
    return () => {
      if (hasTimeout) {
        removeSnackbar(snackbar.id);
      }
    };
  }, [hasTimeout]);

  return (
    <Item
      $isSuccess={snackbar.type === 'success'}
      $isDanger={snackbar.type === 'error'}
      $isWarning={snackbar.type === 'warning'}
      $isInfo={snackbar.type === 'info'}
      $transition={enableTransition}
      ref={ref}
    >
      <Content>{snackbar.reactNode}</Content>
      <Action>
        <Close onClick={onClick} title={t('close', { ns: 'common' }) || ''}>
          <IconCancel />
        </Close>
      </Action>
    </Item>
  );
};

export default SnackbarItem;
